import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Settings as SettingsIcon, Zap, Sun, Moon, LogOut, Crown, Loader2, Receipt } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useMode } from "@/context/ModeContext";
import { PageHeader, Spinner } from "@/components/Bits";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { applyZoom, getZoom, ZOOM_OPTIONS } from "@/lib/zoom";
import { toast } from "sonner";

const PLANS = [
  { id: "free", name: "Free", price: "$0", feats: ["Limited creations", "Basic templates", "Watermark", "Arcade publishing"] },
  { id: "pro", name: "Creator Pro", price: "$9/mo", feats: ["More AI credits", "No watermark", "Advanced templates", "Creator analytics"] },
  { id: "studio", name: "Creator Studio", price: "$29/mo", feats: ["Highest limits", "Team collaboration", "Game jam hosting", "Early export tools"] },
];

export default function Settings() {
  const { user, logout, refresh, patchUser } = useAuth();
  const { mode, toggle } = useMode();
  const navigate = useNavigate();
  const [usage, setUsage] = useState(null);
  const [billing, setBilling] = useState(null);
  const [checkingOut, setCheckingOut] = useState("");
  const [verifying, setVerifying] = useState(false);
  const [params, setParams] = useSearchParams();
  const [zoom, setZoom] = useState(getZoom());
  const pickZoom = (v) => {
    applyZoom(v); setZoom(v);
    if (user) api.patch("/me/prefs", { zoom: v }).then(() => patchUser?.({ zoom_pref: v })).catch(() => {});
  };

  useEffect(() => {
    api.get("/me/usage").then((r) => setUsage(r.data));
    api.get("/billing/me").then((r) => setBilling(r.data)).catch(() => {});
  }, []);

  const pollStatus = useCallback(async (sessionId, attempt = 0) => {
    const MAX = 6;
    if (attempt >= MAX) { setVerifying(false); toast.error("Still processing — refresh in a moment to see your new plan."); return; }
    try {
      const { data } = await api.get(`/billing/status/${sessionId}`);
      if (data.payment_status === "paid") {
        await refresh();
        api.get("/me/usage").then((r) => setUsage(r.data));
        api.get("/billing/me").then((r) => setBilling(r.data)).catch(() => {});
        setVerifying(false);
        toast.success("Payment successful — your plan is upgraded! 🎉");
        return;
      }
      if (data.status === "expired") { setVerifying(false); toast.error("Payment session expired."); return; }
      setTimeout(() => pollStatus(sessionId, attempt + 1), 2000);
    } catch {
      setVerifying(false);
      toast.error("Could not verify payment status.");
    }
  }, [refresh]);

  useEffect(() => {
    const sid = params.get("session_id");
    if (sid) {
      setVerifying(true);
      pollStatus(sid, 0);
      params.delete("session_id");
      setParams(params, { replace: true });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const upgrade = async (planId) => {
    if (planId === "free") return;
    setCheckingOut(planId);
    try {
      const { data } = await api.post("/billing/checkout", { plan_id: planId, origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (e) {
      setCheckingOut("");
      toast.error(e.response?.data?.detail || "Could not start checkout");
    }
  };

  if (!usage) return <Spinner />;
  const pct = usage.credits_total ? (usage.credits_used / usage.credits_total) * 100 : 0;

  return (
    <div className="p-5 lg:p-8 max-w-4xl mx-auto">
      <PageHeader title="Settings" icon={SettingsIcon} />

      {verifying && (
        <div className="mb-5 rounded-2xl border border-[hsl(var(--primary))]/30 bg-[hsl(var(--primary))]/5 p-4 flex items-center gap-3" data-testid="billing-verifying">
          <Loader2 className="w-5 h-5 animate-spin text-[hsl(var(--primary))]" />
          <span className="text-sm font-semibold">Confirming your payment…</span>
        </div>
      )}

      <div className="space-y-5">
        <Card title="Account">
          <Row label="Name" value={user.name} />
          <Row label="Email" value={user.email} />
          <Row label="Role" value={<span className="capitalize">{user.role}</span>} />
          <Row label="Current plan" value={<span className="capitalize font-bold">{user.plan}</span>} />
        </Card>

        <Card title="AI Credits">
          <div className="flex items-center justify-between mb-2">
            <span className="flex items-center gap-2 text-sm"><Zap className="w-4 h-4 text-[hsl(var(--primary))]" /> {usage.credits_remaining} of {usage.credits_total} remaining</span>
            <span className="text-sm text-[hsl(var(--muted-foreground))]">{usage.credits_used} used</span>
          </div>
          <Progress value={pct} className="h-3" data-testid="credits-bar" />
          <p className="text-xs text-[hsl(var(--muted-foreground))] mt-2">Credits reset monthly. Upgrade for higher limits.</p>
        </Card>

        {usage.caps && !usage.quota_exempt && (
          <Card title="Plan usage">
            <div className="space-y-4" data-testid="plan-usage">
              <QuotaBar label="Games created" used={usage.quota_usage?.games || 0} cap={usage.caps.max_games} testid="quota-games" />
              <QuotaBar label="Published games" used={usage.quota_usage?.published || 0} cap={usage.caps.max_published} testid="quota-published" />
              <div className="flex items-center justify-between text-sm">
                <span>Sell on Marketplace</span>
                {usage.caps.marketplace
                  ? <span className="text-emerald-500 font-semibold" data-testid="quota-marketplace">Included</span>
                  : <span className="text-[hsl(var(--muted-foreground))]" data-testid="quota-marketplace">Upgrade to unlock</span>}
              </div>
            </div>
          </Card>
        )}

        <Card title="Appearance">
          <div className="flex items-center justify-between">
            <span className="text-sm">Visual mode: <b className="capitalize">{mode}</b></span>
            <Button variant="outline" onClick={toggle} className="rounded-full" data-testid="settings-mode-toggle">
              {mode === "kid" ? <Moon className="w-4 h-4" /> : <Sun className="w-4 h-4" />} Switch to {mode === "kid" ? "Studio" : "Kid"} Mode
            </Button>
          </div>
          <div className="mt-4 pt-4 border-t border-[hsl(var(--border))] flex items-center justify-between flex-wrap gap-3">
            <div>
              <span className="text-sm font-semibold">Interface zoom</span>
              <p className="text-xs text-[hsl(var(--muted-foreground))]">Pick a comfy size — applies on tablet & desktop, saved on this device.</p>
            </div>
            <div className="inline-flex items-center rounded-full border border-[hsl(var(--border))] p-0.5 text-sm" data-testid="settings-zoom">
              {ZOOM_OPTIONS.map((o) => (
                <button
                  key={o.value}
                  onClick={() => pickZoom(o.value)}
                  data-testid={`settings-zoom-${o.pct}`}
                  className={`px-3 py-1 rounded-full transition-colors ${zoom === o.value ? "bg-[hsl(var(--primary))] text-white font-bold" : "text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))]"}`}
                >
                  {o.pct}%
                </button>
              ))}
            </div>
          </div>
        </Card>

        <Card title="Plans">
          <div className="grid sm:grid-cols-3 gap-3">
            {PLANS.map((p) => (
              <div key={p.id} className={`rounded-xl border p-4 ${user.plan === p.id ? "border-[hsl(var(--primary))] bg-[hsl(var(--primary))]/5" : "border-[hsl(var(--border))]"}`}>
                <div className="flex items-center gap-1.5 font-display font-bold">{p.id !== "free" && <Crown className="w-4 h-4 text-amber-500" />}{p.name}</div>
                <div className="text-2xl font-extrabold my-1">{p.price}</div>
                <ul className="text-xs text-[hsl(var(--muted-foreground))] space-y-1 mb-3">{p.feats.map((f) => <li key={f}>• {f}</li>)}</ul>
                {user.plan === p.id ? <Button size="sm" disabled className="w-full rounded-full">Current</Button> :
                  p.id === "free" ? <Button size="sm" variant="outline" disabled className="w-full rounded-full">Free plan</Button> :
                  <Button size="sm" className="w-full rounded-full bg-gradient-to-r from-[#1f6feb] to-[#3b9eff]" onClick={() => upgrade(p.id)} disabled={!!checkingOut} data-testid={`plan-${p.id}`}>
                    {checkingOut === p.id ? <><Loader2 className="w-4 h-4 animate-spin" /> Redirecting…</> : "Upgrade"}
                  </Button>}
              </div>
            ))}
          </div>
          <p className="text-xs text-[hsl(var(--muted-foreground))] mt-3">Secure payment via Stripe. You'll be redirected to a checkout page.</p>
        </Card>

        <Card title="Billing history">
          {billing?.transactions?.length ? (
            <div className="divide-y divide-[hsl(var(--border))]" data-testid="billing-history">
              {billing.transactions.map((t) => (
                <div key={t.id || t.session_id} className="flex items-center justify-between py-2.5 text-sm" data-testid="billing-history-row">
                  <div className="flex items-center gap-2 min-w-0">
                    <Receipt className="w-4 h-4 text-[hsl(var(--muted-foreground))] shrink-0" />
                    <div className="min-w-0">
                      <div className="font-semibold truncate">{t.plan_name || t.plan_id || "Subscription"}</div>
                      <div className="text-xs text-[hsl(var(--muted-foreground))]">{new Date(t.created_at).toLocaleDateString()}</div>
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="font-bold">${Number(t.amount || 0).toFixed(2)}</div>
                    <div className={`text-xs font-semibold capitalize ${t.payment_status === "paid" ? "text-emerald-500" : "text-[hsl(var(--muted-foreground))]"}`}>{t.payment_status || t.status}</div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-[hsl(var(--muted-foreground))]" data-testid="billing-history-empty">No transactions yet. Upgrade a plan to see your payment history here.</p>
          )}
        </Card>

        <Button variant="destructive" onClick={() => { logout(); navigate("/"); }} className="rounded-full" data-testid="settings-logout"><LogOut className="w-4 h-4" /> Log out</Button>
      </div>
    </div>
  );
}

const Card = ({ title, children }) => (
  <div className="rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-5">
    <div className="font-display font-bold mb-4">{title}</div>{children}
  </div>
);
const QuotaBar = ({ label, used, cap, testid }) => {
  const unlimited = cap === null || cap === undefined || cap < 0;
  const pct = unlimited ? 0 : Math.min(100, (used / Math.max(1, cap)) * 100);
  const atCap = !unlimited && used >= cap;
  return (
    <div data-testid={testid}>
      <div className="flex items-center justify-between text-sm mb-1.5">
        <span>{label}</span>
        <span className={`font-semibold ${atCap ? "text-amber-500" : ""}`}>{used} {unlimited ? "· Unlimited" : `/ ${cap}`}</span>
      </div>
      {!unlimited && <Progress value={pct} className={`h-2 ${atCap ? "[&>div]:bg-amber-500" : ""}`} />}
      {atCap && <p className="text-xs text-amber-500 mt-1">You've hit your plan limit — upgrade to add more.</p>}
    </div>
  );
};
const Row = ({ label, value }) => (
  <div className="flex items-center justify-between py-2 border-b border-[hsl(var(--border))] last:border-0 text-sm">
    <span className="text-[hsl(var(--muted-foreground))]">{label}</span><span className="font-semibold">{value}</span>
  </div>
);
